import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { Loader2, Calculator } from "lucide-react";
import { calcEmi, addMonthsISO } from "../../lib/emi";
import { formatINR } from "../../lib/format";

const today = () => new Date().toISOString().slice(0, 10);
const EMPTY = {
  clf_id: "", vo_id: "", shg_id: "", member_id: "none", principal: "", disbursement_date: today(),
  interest_rate: "", interest_method: "reducing", tenure_months: "", first_emi_date: addMonthsISO(today(), 1),
  status: "active", purpose: "",
};

export const Field = ({ label, required, error, children, full }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <Label className="text-xs font-semibold uppercase tracking-wider text-emerald-900/70">{label}{required && " *"}</Label>
    <div className="mt-1.5">{children}</div>
    {error && <p className="mt-1 text-xs text-rose-700">{error}</p>}
  </div>
);

export const Pick = ({ value, onChange, options, placeholder, testId, disabled }) => (
  <Select value={value || undefined} onValueChange={onChange} disabled={disabled}>
    <SelectTrigger className="h-10 bg-white" data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent className="bg-white">
      {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
    </SelectContent>
  </Select>
);

export const LoanFormDialog = ({ open, onOpenChange, loan, refs, onSubmit }) => {
  const { clfs, vos, shgs, members } = refs;
  const [v, setV] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setV(loan ? { ...EMPTY, ...loan, member_id: loan.member_id || "none", principal: String(loan.principal),
      interest_rate: String(loan.interest_rate), tenure_months: String(loan.tenure_months) } : EMPTY);
  }, [open, loan]);

  const set = (k, val) => setV((p) => ({ ...p, [k]: val }));
  const voOpts = useMemo(() => vos.filter((x) => x.clf_id === v.clf_id).map((x) => ({ value: x.id, label: `${x.name} (${x.code})` })), [vos, v.clf_id]);
  const shgOpts = useMemo(() => shgs.filter((x) => x.vo_id === v.vo_id).map((x) => ({ value: x.id, label: `${x.name} (${x.code})` })), [shgs, v.vo_id]);
  const memOpts = useMemo(() => [{ value: "none", label: "— SHG as borrower —" },
    ...members.filter((x) => x.shg_id === v.shg_id).map((x) => ({ value: x.id, label: `${x.name} (${x.code})` }))], [members, v.shg_id]);
  const calc = calcEmi(v.principal, v.interest_rate, v.tenure_months, v.interest_method);

  const validate = () => {
    const e = {};
    ["clf_id", "vo_id", "shg_id", "disbursement_date", "first_emi_date"].forEach((k) => { if (!v[k]) e[k] = "Required"; });
    if (!(Number(v.principal) > 0)) e.principal = "Enter an amount greater than ₹0";
    if (v.interest_rate === "" || Number(v.interest_rate) < 0 || Number(v.interest_rate) > 60) e.interest_rate = "Enter a rate between 0 and 60";
    if (!(parseInt(v.tenure_months, 10) >= 1) || parseInt(v.tenure_months, 10) > 360) e.tenure_months = "Enter 1–360 months";
    if (v.first_emi_date && v.disbursement_date && v.first_emi_date < v.disbursement_date) e.first_emi_date = "Must be on/after disbursement date";
    setErrors(e);
    return !Object.keys(e).length;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      await onSubmit({ ...v, member_id: v.member_id === "none" ? null : v.member_id, principal: Number(v.principal),
        interest_rate: Number(v.interest_rate), tenure_months: parseInt(v.tenure_months, 10) });
      onOpenChange(false);
    } catch (err) { toast.error(err.message || "Could not save loan"); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto bg-white" data-testid="loan-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-emerald-950">{loan ? `Edit loan ${loan.loan_number}` : "New loan"}</DialogTitle>
          <DialogDescription>{loan ? "Changes recalculate the EMI." : "Loan ID is generated automatically when you save."}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Loan ID" full><Input disabled value={loan?.loan_number || "Auto-generated on save (LN-YYYY-NNNN)"} data-testid="loan-dialog-number" /></Field>
          <Field label="CLF" required error={errors.clf_id}>
            <Pick testId="loan-dialog-clf" value={v.clf_id} placeholder="Select CLF" options={clfs.map((x) => ({ value: x.id, label: x.name }))}
              onChange={(x) => setV((p) => ({ ...p, clf_id: x, vo_id: "", shg_id: "", member_id: "none" }))} />
          </Field>
          <Field label="Village Organization" required error={errors.vo_id}>
            <Pick testId="loan-dialog-vo" value={v.vo_id} placeholder={v.clf_id ? (voOpts.length ? "Select VO" : "No VOs in this CLF") : "Select CLF first"} options={voOpts} disabled={!voOpts.length}
              onChange={(x) => setV((p) => ({ ...p, vo_id: x, shg_id: "", member_id: "none" }))} />
          </Field>
          <Field label="Self Help Group" required error={errors.shg_id}>
            <Pick testId="loan-dialog-shg" value={v.shg_id} placeholder={v.vo_id ? (shgOpts.length ? "Select SHG" : "No SHGs in this VO") : "Select VO first"} options={shgOpts} disabled={!shgOpts.length}
              onChange={(x) => setV((p) => ({ ...p, shg_id: x, member_id: "none" }))} />
          </Field>
          <Field label="Borrower / member">
            <Pick testId="loan-dialog-member" value={v.member_id} options={memOpts} disabled={!v.shg_id} onChange={(x) => set("member_id", x)} />
          </Field>
          <Field label="Principal (₹)" required error={errors.principal}>
            <Input type="number" min="1" step="1" value={v.principal} onChange={(e) => set("principal", e.target.value)} placeholder="e.g. 150000" data-testid="loan-dialog-principal" />
          </Field>
          <Field label="Disbursement date" required error={errors.disbursement_date}>
            <Input type="date" value={v.disbursement_date} onChange={(e) => set("disbursement_date", e.target.value)} data-testid="loan-dialog-disbursement-date" />
          </Field>
          <Field label="Interest rate (% p.a.)" required error={errors.interest_rate}>
            <Input type="number" min="0" max="60" step="0.01" value={v.interest_rate} onChange={(e) => set("interest_rate", e.target.value)} placeholder="e.g. 9" data-testid="loan-dialog-rate" />
          </Field>
          <Field label="Calculation method" required>
            <Pick testId="loan-dialog-method" value={v.interest_method} onChange={(x) => set("interest_method", x)}
              options={[{ value: "reducing", label: "Reducing balance (EMI)" }, { value: "flat", label: "Flat rate" }]} />
          </Field>
          <Field label="Tenure (months)" required error={errors.tenure_months}>
            <Input type="number" min="1" max="360" step="1" value={v.tenure_months} onChange={(e) => set("tenure_months", e.target.value)} placeholder="e.g. 24" data-testid="loan-dialog-tenure" />
          </Field>
          <Field label="First EMI due date" required error={errors.first_emi_date}>
            <Input type="date" value={v.first_emi_date} onChange={(e) => set("first_emi_date", e.target.value)} data-testid="loan-dialog-first-emi" />
          </Field>
          <Field label="Loan status" required>
            <Pick testId="loan-dialog-status" value={v.status} onChange={(x) => set("status", x)}
              options={[{ value: "pending", label: "Pending" }, { value: "active", label: "Active" }, { value: "closed", label: "Closed" }]} />
          </Field>
          <Field label="Purpose" full>
            <Input value={v.purpose} onChange={(e) => set("purpose", e.target.value)} placeholder="e.g. Handloom weaving" data-testid="loan-dialog-purpose" />
          </Field>
          <div className="grid grid-cols-3 gap-3 rounded-xl border border-emerald-900/15 bg-emerald-50/60 p-4 sm:col-span-2" data-testid="loan-dialog-emi-preview">
            <div className="col-span-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-emerald-900/70"><Calculator className="h-4 w-4" />Calculated</div>
            {[["Monthly EMI", calc?.emi, "loan-dialog-emi"], ["Total interest", calc?.totalInterest], ["Total payable", calc?.totalPayable]].map(([l, val, tid]) => (
              <div key={l}><div className="text-[11px] text-emerald-900/60">{l}</div>
                <div className="font-mono-tabular text-base font-bold text-emerald-950" data-testid={tid}>{calc ? formatINR(val) : "—"}</div></div>
            ))}
          </div>
          <DialogFooter className="mt-1 sm:col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="loan-dialog-cancel">Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-[#0F4C3A] text-white hover:bg-emerald-900" data-testid="loan-dialog-submit">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{loan ? "Save changes" : "Create loan"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
